import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import { useState, useRef, useEffect } from "react";
import "./table.styles.css";
import { globalTheme } from "../../styles";
import { shipmentTableColumnsData } from "./shipmentTableColumns";
import { AUTHENTICATED_EMPLOYEE_JWT, PAGINATION_PAGE_COUNT_OPTIONS, SHIPMENTS_TAB } from "../../constants";
import { Box, Divider, MenuItem, Select, Stack, Typography } from "@mui/material";
import { LeftArrow, RightArrow } from "../Icon";
import { useSearchOrderShipments } from "../../hooks";

export const ShipmentsTable = ({
  searchFilters,
  bundleView,
  searchOrderShipmentsText,
  orderShipmentsSearchType,
  searchSortOption,
  setSearchOrderShipmentsResponse,
  setErrorShipmentOrderResponse,
  setSnackBar,
}) => {
  const gridRef = useRef(null);
  const [orderShipmentsSearchPageNumber, setOrderShipmentsSearchPageNumber] = useState(1);
  const [paginationPageSize, setPaginationPageSize] = useState(PAGINATION_PAGE_COUNT_OPTIONS[2]);

  const [enableSearchOrderShipments, setEnableSearchOrderShipments] = useState(false);
  const [totalOrders, setTotalOrders] = useState<number | null>(null);

  const shipmentsTab = localStorage.getItem(SHIPMENTS_TAB);

  // Fetch order shipment details
  const {
    searchOrderShipmentsResponse,
    fetchingSearchOrderShipments,
    refetchSearchOrderShipments,
    fetchSearchOrderShipmentsError,
  } = useSearchOrderShipments({
    searchOptions: {
      filters: searchFilters,
      pageNumber: orderShipmentsSearchPageNumber,
      pageSize: paginationPageSize,
      searchText: searchOrderShipmentsText,
      searchType: orderShipmentsSearchType,
      sortOption: searchSortOption,
    },
    enabled: enableSearchOrderShipments,
  });

  useEffect(() => {
    if (searchOrderShipmentsResponse?.code === "UNAUTHORIZED") {
      localStorage.removeItem(AUTHENTICATED_EMPLOYEE_JWT);
      window.location.reload();
    }
  }, [searchOrderShipmentsResponse]);

  // Passing the state and refetch function to the parent component
  useEffect(() => {
    setSearchOrderShipmentsResponse(searchOrderShipmentsResponse);
    setErrorShipmentOrderResponse(fetchSearchOrderShipmentsError);
  }, [searchOrderShipmentsResponse, fetchSearchOrderShipmentsError]);

  const [rowData, setRowData] = useState(searchOrderShipmentsResponse?.orders);

  // Refetch data on search filters change
  useEffect(() => {
    setEnableSearchOrderShipments(true);
  }, [searchOrderShipmentsText, searchFilters, searchSortOption, orderShipmentsSearchPageNumber, paginationPageSize]);

  useEffect(() => {
    if (searchOrderShipmentsResponse) {
      setEnableSearchOrderShipments(false);
    }
  }, [searchOrderShipmentsResponse]);

  const [columnDefs, setColumnDefs] = useState<ColDef[]>(() =>
    shipmentTableColumnsData({
      onRefreshData: () => refetchSearchOrderShipments(),
      setSnackBar,
      shipmentTab: shipmentsTab,
    }).map((col) => ({
      ...col,
    }))
  );

  useEffect(() => {
    setColumnDefs(
      shipmentTableColumnsData({
        onRefreshData: () => refetchSearchOrderShipments(),
        setSnackBar,
        shipmentTab: shipmentsTab,
      }).map((col) => ({
        ...col,
      }))
    );
  }, [bundleView, shipmentsTab]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      const api = gridRef.current.api;
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();
      setRowData(searchOrderShipmentsResponse?.orders);
      setTimeout(() => {
        api.ensureIndexVisible(lastDisplayedRowIndex, "top");
      }, 0);
    }
  }, [searchOrderShipmentsResponse]);

  // Show loading or no rows overlay based on the state
  useEffect(() => {
    if (searchOrderShipmentsResponse?.totalOrders) {
      setTotalOrders(searchOrderShipmentsResponse?.totalOrders);
    }
    if (gridRef.current && gridRef.current.api) {
      const api = gridRef.current.api;
      if (fetchingSearchOrderShipments) {
        api.showLoadingOverlay();
      } else if (!rowData || rowData.length === 0) {
        api.showNoRowsOverlay();
      } else {
        api.hideOverlay();
      }
    }
  }, [rowData, fetchingSearchOrderShipments]);

  // Modify only the "Order ID" column during onGridReady
  const onGridReady = (params) => {
    const api = params.api;

    const updatedColumnDefs = shipmentTableColumnsData({
      onRefreshData: () => refetchSearchOrderShipments(),
      setSnackBar,
      shipmentTab: shipmentsTab,
    }).map((col) => {
      if (col.field === "OrderID") {
        return {
          ...col,
          headerComponentParams: { displayName: col.headerName, api },
        };
      }
      return col;
    });

    setColumnDefs(updatedColumnDefs);
  };

  const handlePaginationPageSizeChange = (event) => {
    setPaginationPageSize(event.target.value);
  };
  // Refetch data upon filters update
  useEffect(() => {
    refetchSearchOrderShipments();
  }, [searchFilters, paginationPageSize, searchOrderShipmentsText, orderShipmentsSearchType, searchSortOption]);

  return (
    <div>
      <div className={"ag-theme-alpine"} style={{ height: "72vh", minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          onGridReady={onGridReady} // Handle grid ready event
          onRowSelected={(event) => {
            // console.log("Selected rows", event.api.getSelectedRows()); // Use event.api instead of gridRef
          }}
          overlayLoadingTemplate={
            '<div aria-live="polite" aria-atomic="true" style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
          }
          overlayNoRowsTemplate={`<span aria-live="polite" aria-atomic="true" style="padding: 10px; background: ${globalTheme.background}">No data available for the selected filters.</span>`}
        />
      </div>
      {/** Pagination */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} marginTop={2} paddingTop={1}>
        <Typography variant="caption">{`Total orders: ${totalOrders}`}</Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="caption">Page size:</Typography>
        <Select
          labelId="pagination-options-label-id"
          id="pagination-options-id"
          value={paginationPageSize}
          onChange={handlePaginationPageSizeChange}
          sx={{ ".MuiSelect-select": { padding: 1, width: "25px" }, fontSize: "0.75rem" }}
        >
          {PAGINATION_PAGE_COUNT_OPTIONS.map((value, key) => (
            <MenuItem value={value} key={`${key}-menu-item`}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            </MenuItem>
          ))}
        </Select>
        <Box onClick={() => setOrderShipmentsSearchPageNumber((prev) => (prev === 1 ? prev : prev - 1))}>
          <LeftArrow />
        </Box>
        <Typography variant="caption">{`${orderShipmentsSearchPageNumber} of ${Math.ceil(
          totalOrders / paginationPageSize
        )}`}</Typography>
        <Box onClick={() => setOrderShipmentsSearchPageNumber((prev) => prev + 1)}>
          <RightArrow />
        </Box>
      </Stack>
    </div>
  );
};
